/**
 * Google Analytics (GA4) Event Tracking Utility
 * Use this to track custom events throughout the CampYid app
 */

import { BACKEND_URL } from "@/config/runtime";

// Check if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event (e.g., 'sign_up', 'purchase')
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track page view (for SPA navigation)
 * @param {string} pagePath - The page path (e.g., '/listing/vendor-name')
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-99MX460PXD', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.origin + pagePath
    });
  }
  
  // Check if this is a new session (first page view in this browser tab/session)
  const sessionKey = '_cy_session_tracked';
  const isNewSession = !sessionStorage.getItem(sessionKey);
  if (isNewSession) {
    sessionStorage.setItem(sessionKey, '1');
  }

  // Track in backend for admin analytics
  fetch(`${getBackendUrl()}/api/analytics/page-view`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ is_new_session: isNewSession })
  }).catch(() => {}); // Silent fail for analytics
};

// ============== PREDEFINED EVENT TRACKERS ==============

/**
 * Track user sign up
 * @param {string} method - Sign up method ('email' or 'google')
 * @param {string} userType - User type ('camp' or 'vendor')
 */
export const trackSignUp = (method, userType) => {
  trackEvent('sign_up', {
    method: method,
    user_type: userType
  });
};

/**
 * Track user login
 * @param {string} method - Login method ('email' or 'google')
 */
export const trackLogin = (method) => {
  trackEvent('login', {
    method: method
  });
};

// Get backend URL for API calls - uses runtime config for production support
const getBackendUrl = () => {
  return BACKEND_URL;
};

/**
 * Track listing view
 * @param {string} listingId - The listing ID
 * @param {string} listingTitle - The listing title
 * @param {string} category - The listing category
 */
export const trackListingView = (listingId, listingTitle, category) => {
  // Track in Google Analytics
  trackEvent('view_item', {
    item_id: listingId,
    item_name: listingTitle,
    item_category: category
  });
  
  // Track in backend database
  fetch(`${getBackendUrl()}/api/listings/${listingId}/track-view`, {
    method: 'POST',
    credentials: 'include'
  }).catch(() => {}); // Silent fail for analytics
};

/**
 * Track inquiry submission
 * @param {string} listingId - The listing ID
 * @param {string} vendorName - The vendor name
 */
export const trackInquiry = (listingId, vendorName) => {
  trackEvent('generate_lead', {
    item_id: listingId,
    item_name: vendorName,
    currency: 'USD'
  });
};

/**
 * Track listing saved to favorites
 * @param {string} listingId - The listing ID
 * @param {string} listingTitle - The listing title
 */
export const trackSaveListing = (listingId, listingTitle) => {
  trackEvent('add_to_wishlist', {
    item_id: listingId,
    item_name: listingTitle
  });
  // Note: tap tracking is now handled when drawer/page opens, not on save action
};

/**
 * Track search performed
 * @param {string} searchTerm - The search query
 * @param {number} resultsCount - Number of results returned
 */
export const trackSearch = (searchTerm, resultsCount) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Track category browsed
 * @param {string} categoryName - The category name
 */
export const trackCategoryView = (categoryName) => {
  trackEvent('view_item_list', {
    item_list_name: categoryName
  });
};

/**
 * Track payment initiated
 * @param {string} planType - The plan type
 * @param {number} value - The payment amount
 */
export const trackBeginCheckout = (planType, value) => {
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: value,
    items: [{
      item_name: planType,
      price: value
    }]
  });
};

/**
 * Track successful purchase
 * @param {string} transactionId - The transaction ID
 * @param {string} planType - The plan type
 * @param {number} value - The payment amount
 */
export const trackPurchase = (transactionId, planType, value) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value: value,
    items: [{
      item_name: planType,
      price: value
    }]
  });
};

/**
 * Track listing created by vendor
 * @param {string} listingId - The listing ID
 * @param {string} category - The listing category
 */
export const trackListingCreated = (listingId, category) => {
  trackEvent('listing_created', {
    listing_id: listingId,
    category: category
  });
};

/**
 * Track testimonial submitted
 * @param {string} listingId - The listing ID
 */
export const trackTestimonialSubmit = (listingId) => {
  trackEvent('testimonial_submitted', {
    listing_id: listingId
  });
};

/**
 * Track session duration for site analytics
 * Call this when user leaves or when session ends
 * @param {number} durationSeconds - Session duration in seconds
 */
export const trackSessionDuration = (durationSeconds) => {
  // Use sendBeacon for reliable delivery on page unload
  const url = `${getBackendUrl()}/api/analytics/session`;
  const data = JSON.stringify({ duration: durationSeconds });
  
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
  } else {
    // Fallback for older browsers
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data,
      credentials: 'include',
      keepalive: true
    }).catch(() => {});
  }
};

/**
 * Initialize session tracking - call once on app load
 * Automatically tracks session duration when user leaves
 */
export const initSessionTracking = () => {
  const sessionStart = Date.now();
  
  const sendSessionDuration = () => {
    const durationSeconds = Math.round((Date.now() - sessionStart) / 1000);
    if (durationSeconds > 5) { // Only track sessions longer than 5 seconds
      trackSessionDuration(durationSeconds);
    }
  };
  
  // Track on page visibility change (user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      sendSessionDuration();
    }
  });
  
  // Track on page unload
  window.addEventListener('beforeunload', sendSessionDuration);
  window.addEventListener('pagehide', sendSessionDuration);
};

export default {
  trackEvent,
  trackPageView,
  trackSignUp,
  trackLogin,
  trackListingView,
  trackInquiry,
  trackSaveListing,
  trackSearch,
  trackCategoryView,
  trackBeginCheckout,
  trackPurchase,
  trackListingCreated,
  trackTestimonialSubmit,
  trackSessionDuration,
  initSessionTracking
};
